/**
 * WorkflowTestPanel Component
 *
 * Panel que exibe o progresso de teste de workflow em tempo real.
 * Mostra status de execução, nodes sendo processados e resultados.
 *
 * Segue o mesmo padrão visual do ExecutionHistorySidebar e NodeConfigurator (w-80).
 */

import React, { useMemo } from 'react';
import type {
  TestProgress,
  NodeStatusInfo,
} from '../hooks/useWorkflowTestSocket';
import { describeSkipReason, SKIPPED_REASON_TITLES } from '../utils/skip-reasons';
import { Icon } from '@/shared/icons'
import type { IconName } from '@/shared/icons'
// Status colors for node execution states
const NODE_STATUS_COLORS = {
  idle: '#9ca3af',
  running: '#3b82f6',
  completed: '#10b981',
  failed: '#ef4444',
  skipped: '#f59e0b',
} as const;

// Status icons for node execution states
const NODE_STATUS_ICONS = {
  idle: 'clock',
  running: 'loader',
  completed: 'check-circle',
  failed: 'x-circle',
  skipped: 'skip-forward',
} as const;

// Status labels in Portuguese
const NODE_STATUS_LABELS = {
  idle: 'Aguardando',
  running: 'Executando',
  completed: 'Concluído',
  failed: 'Falhou',
  skipped: 'Ignorado',
} as const;

// Test status labels in Portuguese
const TEST_STATUS_LABELS = {
  idle: 'Pronto para testar',
  running: 'Executando teste...',
  completed: 'Teste concluído',
  failed: 'Teste falhou',
} as const;

export interface WorkflowTestPanelProps {
  /** Progress state from useWorkflowTestSocket */
  testProgress: TestProgress;
  /** Map of node statuses */
  nodeStatuses: Map<string, NodeStatusInfo>;
  /** Whether socket is connected */
  isConnected: boolean;
  /** Callback to close the panel */
  onClose: () => void;
  /** Callback to reset/restart test */
  onReset: () => void;
  /** Callback to start a new test */
  onStartTest?: () => void;
  /** Whether a test is currently starting */
  isStartingTest?: boolean;
}

/**
 * Formata a duração em milissegundos para uma string legível
 */
function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}m ${seconds}s`;
}

/**
 * WorkflowTestPanel - Panel para exibir progresso de teste de workflow
 */
export const WorkflowTestPanel: React.FC<WorkflowTestPanelProps> = ({
  testProgress,
  nodeStatuses,
  isConnected,
  onClose,
  onReset,
  onStartTest,
  isStartingTest,
}) => {
  // Calculate progress percentage
  const progressPercentage = useMemo(() => {
    if (testProgress.totalNodes === 0) return 0;
    const processed =
      testProgress.nodesExecuted + testProgress.nodesFailed + testProgress.nodesSkipped;
    return Math.round((processed / testProgress.totalNodes) * 100);
  }, [testProgress]);

  // Get sorted node statuses for display
  const sortedNodeStatuses = useMemo(() => {
    return Array.from(nodeStatuses.values()).sort((a, b) => {
      // Running nodes first
      if (a.status === 'running' && b.status !== 'running') return -1;
      if (b.status === 'running' && a.status !== 'running') return 1;
      // Then by order (ascending - first executed first)
      const aOrder = a.order ?? 0;
      const bOrder = b.order ?? 0;
      return aOrder - bOrder;
    });
  }, [nodeStatuses]);

  // Get test status color
  const testStatusColor = useMemo(() => {
    switch (testProgress.status) {
      case 'running':
        return '#3b82f6';
      case 'completed':
        return '#10b981';
      case 'failed':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  }, [testProgress.status]);

  return (
    <div
      className="flex flex-col md:w-[400px] md:max-w-full md:border-l md:h-full max-md:fixed max-md:inset-x-0 max-md:bottom-0 max-md:z-[70] max-md:max-h-[75vh] max-md:rounded-t-2xl max-md:border-t max-md:shadow-[0_-8px_30px_rgba(0,0,0,0.18)] max-md:[background:var(--theme-overlay-card-background)] md:[background-color:var(--theme-card-background)]"
      style={{
        borderColor: 'var(--theme-card-border)',
      }}
    >
      {/* Header */}
      <div
        className="px-4 py-3 border-b flex items-center justify-between flex-shrink-0"
        style={{ borderColor: 'var(--theme-card-border)' }}
      >
        <div className="flex items-center gap-2">
          <Icon name="play" className="w-5 h-5" style={{ color: 'var(--theme-muted-text)' }} />
          <h2 className="font-semibold text-base" style={{ color: 'var(--theme-text)' }}>
            Teste de Workflow
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-lg transition-colors"
          style={{ color: 'var(--theme-muted-text)' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
            e.currentTarget.style.color = 'var(--theme-text)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--theme-muted-text)';
          }}
        >
          <Icon name="x" className="w-5 h-5" />
        </button>
      </div>

      {/* Connection Status */}
      {!isConnected && (
        <div
          className="px-4 py-2 border-b flex items-center gap-2 flex-shrink-0"
          style={{
            backgroundColor: 'var(--theme-error-subtle)',
            borderColor: 'var(--theme-card-border)',
          }}
        >
          <Icon name="alert-circle" className="w-4 h-4" style={{ color: 'var(--theme-error-color)' }} />
          <span className="text-xs font-medium" style={{ color: 'var(--theme-error-color)' }}>
            Desconectado do servidor
          </span>
        </div>
      )}

      {/* Test Status Banner */}
      <div
        className="px-4 py-3 border-b flex-shrink-0"
        style={{
          backgroundColor: `${testStatusColor}10`,
          borderColor: 'var(--theme-card-border)',
        }}
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium" style={{ color: testStatusColor }}>
            {TEST_STATUS_LABELS[testProgress.status]}
          </span>
          {testProgress.duration !== null && (
            <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
              {formatDuration(testProgress.duration)}
            </span>
          )}
        </div>

        {/* Progress Bar */}
        {testProgress.status === 'running' && (
          <div
            className="w-full h-2 rounded-full overflow-hidden"
            style={{ backgroundColor: 'var(--theme-surface-sunken)' }}
          >
            <div
              className="h-full transition-all duration-300"
              style={{
                width: `${progressPercentage}%`,
                backgroundColor: testStatusColor,
              }}
            />
          </div>
        )}

        {/* Progress Stats */}
        {testProgress.totalNodes > 0 && (
          <div className="flex items-center gap-3 mt-2">
            <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
              {testProgress.nodesExecuted + testProgress.nodesFailed + testProgress.nodesSkipped}/
              {testProgress.totalNodes} nodes
            </span>
            {testProgress.nodesExecuted > 0 && (
              <span
                className="text-xs flex items-center gap-1"
                style={{ color: NODE_STATUS_COLORS.completed }}
              >
                <Icon name="check-circle" className="w-3 h-3" />
                {testProgress.nodesExecuted}
              </span>
            )}
            {testProgress.nodesFailed > 0 && (
              <span
                className="text-xs flex items-center gap-1"
                style={{ color: NODE_STATUS_COLORS.failed }}
              >
                <Icon name="x-circle" className="w-3 h-3" />
                {testProgress.nodesFailed}
              </span>
            )}
            {testProgress.nodesSkipped > 0 && (
              <span
                className="text-xs flex items-center gap-1"
                style={{ color: NODE_STATUS_COLORS.skipped }}
              >
                <Icon name="skip-forward" className="w-3 h-3" />
                {testProgress.nodesSkipped}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Error Message — várias linhas, sem cortar (U3 / D20) */}
      {testProgress.error && (
        <div
          className="px-4 py-3 border-b flex-shrink-0"
          style={{
            backgroundColor: 'var(--theme-error-subtle)',
            borderColor: 'var(--theme-card-border)',
          }}
        >
          <p
            data-testid="workflow-test-panel-error"
            className="text-xs whitespace-pre-wrap break-words"
            style={{ color: 'var(--theme-error-color)' }}
          >
            <span className="font-medium">Erro:</span> {testProgress.error}
          </p>
        </div>
      )}

      {/* Content - Node Status List */}
      <div className="flex-1 overflow-y-auto">
        {/* Empty State - No test running */}
        {testProgress.status === 'idle' && sortedNodeStatuses.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <Icon name="play" className="w-8 h-8" style={{ color: 'var(--theme-card-muted)' }} />
            <p className="mt-2 text-sm text-center" style={{ color: 'var(--theme-muted-text)' }}>
              Nenhum teste em andamento
            </p>
            <p className="mt-1 text-xs text-center" style={{ color: 'var(--theme-card-muted)' }}>
              Clique no botão "Testar" para iniciar
            </p>
          </div>
        )}

        {/* Node Status List */}
        {sortedNodeStatuses.length > 0 && (
          <div className="divide-y" style={{ borderColor: 'var(--theme-card-border)' }}>
            {sortedNodeStatuses.map((nodeStatus) => {
              const statusIcon = (NODE_STATUS_ICONS[nodeStatus.status] || 'clock') as IconName;
              const statusColor = NODE_STATUS_COLORS[nodeStatus.status] || '#6b7280';
              const statusLabel = NODE_STATUS_LABELS[nodeStatus.status] || nodeStatus.status;
              const isRunning = nodeStatus.status === 'running';
              const motivoDoOutput =
                nodeStatus.status === 'skipped' &&
                nodeStatus.output !== null &&
                typeof nodeStatus.output === 'object' &&
                typeof (nodeStatus.output as Record<string, unknown>)['reason'] === 'string'
                  ? ((nodeStatus.output as Record<string, unknown>)['reason'] as string)
                  : undefined;
              // Menor (c) da revisão final da Fase 4 (16/09/2026): o socket
              // manda o motivo em DOIS campos (`useWorkflowTestSocket.ts`:
              // `error: data.reason` e `output`) — se `output.reason` não vier
              // (backend anterior à Fase 3, ou payload do `bail` mudou), cai
              // pro `error` só quando ele já é um dos dois motivos conhecidos
              // (nunca mostra texto de erro cru como "motivo").
              const erroConhecido =
                nodeStatus.status === 'skipped' &&
                typeof nodeStatus.error === 'string' &&
                nodeStatus.error in SKIPPED_REASON_TITLES;
              const motivo = motivoDoOutput ?? (erroConhecido ? nodeStatus.error : undefined);

              return (
                <div
                  key={nodeStatus.nodeId}
                  className="px-4 py-3"
                  style={{
                    backgroundColor:
                      nodeStatus.status === 'running'
                        ? 'var(--theme-info-subtle)'
                        : 'transparent',
                  }}
                >
                  <div className="flex items-start gap-3">
                    {/* Status Icon */}
                    <div
                      className="p-1.5 rounded-full flex-shrink-0 mt-0.5"
                      style={{
                        backgroundColor: `${statusColor}20`,
                      }}
                    >
                      <Icon
                        name={statusIcon}
                        className={`w-4 h-4 ${isRunning ? 'animate-spin' : ''}`}
                        style={{ color: statusColor }}
                      />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      {/* Node Label + Status */}
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className="text-sm font-medium truncate"
                          style={{ color: 'var(--theme-text)' }}
                        >
                          {nodeStatus.nodeLabel || nodeStatus.nodeType || nodeStatus.nodeId}
                        </span>
                        <span className="text-xs flex-shrink-0" style={{ color: statusColor }}>
                          {statusLabel}
                        </span>
                      </div>

                      {/* Node Type */}
                      {nodeStatus.nodeType && nodeStatus.nodeLabel && (
                        <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--theme-card-muted)' }}>
                          {nodeStatus.nodeType}
                        </p>
                      )}

                      {/* Duration */}
                      {nodeStatus.duration !== undefined && (
                        <p className="text-xs mt-1" style={{ color: 'var(--theme-card-muted)' }}>
                          Duração: {formatDuration(nodeStatus.duration)}
                        </p>
                      )}

                      {/* Error — várias linhas, sem cortar e sem atributo de tooltip nativo (U3 / D20) */}
                      {nodeStatus.error && nodeStatus.status !== 'skipped' && (
                        <p
                          data-testid={`test-node-error-${nodeStatus.nodeId}`}
                          className="text-xs mt-1 whitespace-pre-wrap break-words"
                          style={{ color: 'var(--theme-error-color)' }}
                        >
                          {nodeStatus.error}
                        </p>
                      )}

                      {/* Motivo de "Não rodou" (aba Filtros / cancelamento) — Fase 4 */}
                      {nodeStatus.status === 'skipped' && (
                        <p
                          data-testid="test-node-skip-reason"
                          className="text-xs mt-1"
                          style={{ color: 'var(--theme-card-muted)' }}
                        >
                          {describeSkipReason(motivo)}
                        </p>
                      )}

                      {/* Output Preview */}
                      {nodeStatus.output && nodeStatus.status === 'completed' && (
                        <div
                          className="mt-2 p-2 rounded text-xs"
                          style={{
                            backgroundColor: 'var(--theme-surface-sunken)',
                            color: 'var(--theme-card-text)',
                          }}
                        >
                          <pre className="whitespace-pre-wrap break-words">
                            {typeof nodeStatus.output === 'string'
                              ? nodeStatus.output.substring(0, 100)
                              : JSON.stringify(nodeStatus.output, null, 2).substring(0, 100)}
                            {(typeof nodeStatus.output === 'string'
                              ? nodeStatus.output.length
                              : JSON.stringify(nodeStatus.output).length) > 100
                              ? '...'
                              : ''}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div
        className="px-4 py-3 border-t flex items-center justify-between gap-2 flex-shrink-0"
        style={{
          backgroundColor: 'var(--theme-surface-sunken)',
          borderColor: 'var(--theme-card-border)',
        }}
      >
        {/* Reset Button */}
        <button
          type="button"
          onClick={onReset}
          disabled={testProgress.status === 'running'}
          className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors"
          style={{
            color: testProgress.status === 'running' ? 'var(--theme-card-muted)' : 'var(--theme-muted-text)',
            backgroundColor: 'transparent',
            cursor: testProgress.status === 'running' ? 'not-allowed' : 'pointer',
          }}
          onMouseEnter={(e) => {
            if (testProgress.status !== 'running') {
              e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <Icon name="refresh" className="w-4 h-4" />
          Limpar
        </button>

        {/* Start Test Button */}
        {onStartTest && (
          <button
            type="button"
            onClick={onStartTest}
            disabled={testProgress.status === 'running' || isStartingTest || !isConnected}
            className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-lg transition-colors"
            style={{
              backgroundColor:
                testProgress.status === 'running' || isStartingTest || !isConnected
                  ? 'var(--theme-card-muted)'
                  : 'var(--theme-button-primary)',
              color: 'var(--theme-button-primary-text)',
              cursor:
                testProgress.status === 'running' || isStartingTest || !isConnected
                  ? 'not-allowed'
                  : 'pointer',
            }}
            onMouseEnter={(e) => {
              if (
                testProgress.status !== 'running' &&
                !isStartingTest &&
                isConnected
              ) {
                e.currentTarget.style.backgroundColor = 'var(--theme-button-primary-hover)';
              }
            }}
            onMouseLeave={(e) => {
              if (
                testProgress.status !== 'running' &&
                !isStartingTest &&
                isConnected
              ) {
                e.currentTarget.style.backgroundColor = 'var(--theme-button-primary)';
              }
            }}
          >
            {isStartingTest || testProgress.status === 'running' ? (
              <>
                <Icon name="loader" className="w-4 h-4 animate-spin" />
                Executando...
              </>
            ) : (
              <>
                <Icon name="play" className="w-4 h-4" />
                Testar Workflow
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

WorkflowTestPanel.displayName = 'WorkflowTestPanel';

export default WorkflowTestPanel;
